import type { ColumnConfig } from '@/entities/data-export'
import type {
	FieldSchemaModel,
	FilterRuleModel,
	HTTPValidationError,
	ProductsQueryRequestModel,
} from '@/shared/generated'
import type {
	StoredColumnConfig,
	TableConfigState,
} from '@/shared/tools/columns'
import type { AxiosError } from 'axios'
import type { ExportFieldsFormData } from './ExportFieldsModal'
import type { ExportParamsFormData } from './ExportParamsModal'

import React, { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { all, find, isEqual, keyBy, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useLocalStorage } from 'usehooks-ts'
import { ZodError } from 'zod'

import { useColumnsSchemaQuery } from '@/entities/column'
import {
	useCreateExportMutation,
	useExportTemplatesQuery,
} from '@/entities/data-export'

import { ExportGroupBy, ExportType, HiddenOnContext } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { getTableStateSnapshot } from '@/shared/tools/columns'

import {
	getEnabledColumnsCount,
	getReferenceFields,
	mergeConfigs,
	schemaToConfig,
	storedToConfig,
} from '../lib/helpers'
import ExportConfigurationProvider from './ExportConfigurationProvider'
import ExportModal from './ExportModal'

type ExportAssortmentModalProps = {
	requireCallback: boolean
	/**
	 * Export context data (filters, order_by, search)
	 */
	context: Partial<
		Pick<ProductsQueryRequestModel, 'filters' | 'order_by' | 'search'>
	>
	/**
	 * Default export name
	 */
	defaultName?: string

	/**
	 * Table full configuration (column visibility, pinning, order, size)
	 */
	tableConfig: TableConfigState
	isOpen: boolean
	close(): void
}
const DATA_KIND = 'assortment'

export default function ExportAssortmentModal({
	tableConfig,
	...props
}: ExportAssortmentModalProps) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const [customExportColumns, setCustomExportColumns] = useLocalStorage<
		ColumnConfig[]
	>('export_fields_og_assortment', [])
	const schemaQuery = useColumnsSchemaQuery(HiddenOnContext.Export)
	const exportTemplates = useExportTemplatesQuery()
	const createExport = useCreateExportMutation({
		onSuccess() {
			queryClient.invalidateQueries({
				queryKey: ['exports'],
			})
		},
	})
	const schemaMap = useMemo(
		() => keyBy('id', schemaQuery.data),
		[schemaQuery.data]
	)
	const fieldsPresetOptions = useMemo(() => {
		const enrichData = (field: ColumnConfig) => {
			const schema = schemaMap[field.key]

			return {
				...field,
				reference: field.reference || getReferenceFields(schema?.reference),
			}
		}
		const initialConfig = schemaToConfig(schemaQuery.data || [], t)
		const columnsConfigs = getTableStateSnapshot(tableConfig, schemaQuery.data)
		const storedConfig = storedToConfig(
			columnsConfigs.filter(({ key }) => schemaMap[key]),
			schemaQuery.data || [],
			t
		)

		const storedPreset = storedConfig?.length
			? mergeConfigs(initialConfig, storedConfig)
			: initialConfig
		const customPreset = customExportColumns?.length
			? mergeConfigs(initialConfig, customExportColumns)
			: initialConfig

		return [
			{
				value: 'all',
				label:
					t('app.all_columns', { defaultValue: 'All columns' }) +
					` (${getEnabledColumnsCount(initialConfig)})`,
				preset: initialConfig.map(enrichData),
			},
			{
				value: 'table',
				label:
					t('app.current_table_state', {
						defaultValue: 'Current table state',
					}) + ` (${getEnabledColumnsCount(storedPreset)})`,
				preset: storedPreset.map(enrichData),
			},
			{
				value: 'custom',
				label:
					t('app.custom_columns', { defaultValue: 'Custom columns' }) +
					` (${getEnabledColumnsCount(customPreset)})`,
				preset: customPreset.map(enrichData),
			},
			...(exportTemplates.data?.map((template) => {
				const preset = schemaMap
					? mergeConfigs(
							initialConfig,
							template.columns.map((column) => {
								const schema = schemaMap[column.name!]

								return {
									key: column.name,
									type: schema?.value_type || 'string',
									name: column.header,
									custom_name: column.header,
									static_value: column.static_value,
									reference: getReferenceFields(schema?.reference),
									enabled: true,
								} as ColumnConfig
							})
						)
					: []

				return {
					value: template.id,
					label: template.name + ` (${getEnabledColumnsCount(preset)})`,
					preset: preset.map(enrichData),
				}
			}) || []),
		]
	}, [schemaQuery.data, tableConfig, customExportColumns, exportTemplates.data])
	const handleSubmit = (
		formData: ExportParamsFormData &
			ExportFieldsFormData & { exportDestination: string }
	) => {
		// Save custom columns if they are changed
		if (
			formData.exportDestination === 'local' &&
			isAllColumnsExisted(schemaMap, formData.fields) &&
			!isEqual(
				find({ value: formData.fields_preset }, fieldsPresetOptions)?.preset,
				formData.fields
			)
		) {
			setCustomExportColumns(formData.fields as ColumnConfig[])
		}

		ampli.exportModalCreateExport({
			export_kind: DATA_KIND,
			name: formData.name,
			fields_preset: formData.fields_preset,
			format: formData.format,
			aggregation: formData.aggregation,
			exportDestination: formData.exportDestination,
			fields: formData.fields.map((f) => f.key),
		})

		createExport
			.mutateAsync(
				formData.exportDestination === 'local'
					? {
							require_callback: props.requireCallback,
							name: formData.name,
							type: formData.format as ExportType,
							filters: (props.context.filters as FilterRuleModel[]) || [],
							group_by: formData.aggregation as ExportGroupBy,
							columns: formData.fields.filter(prop('enabled')).map((field) => ({
								name: field.key,
								header: field.custom_name,
								static_value: field.static_value || null,
								reference: field.reference || null,
							})),
						}
					: {
							require_callback: props.requireCallback,
							name: formData.name,
							type: ExportType.Bigquery,
							filters: (props.context.filters as FilterRuleModel[]) || [],
							group_by: ExportGroupBy.PricingLine,
							columns: formData.fields.filter(prop('enabled')).map((field) => ({
								name: field.key,
								header: field.custom_name,
								static_value: field.static_value || null,
								reference: field.reference || null,
							})),
						}
			)
			.then((res) => {
				toast.loading(
					t('export.started.body', {
						name: res.name,
						defaultValue: `Export "${res.name}" was started`,
					}),
					{ id: res.id, duration: 9999999999 }
				)
			})
			.catch((error: AxiosError<HTTPValidationError> | ZodError) => {
				if (error instanceof ZodError) {
					toast.error(error.issues.map((issue) => issue.message).join('\n'))
				} else {
					toast.error(
						t('export.failed.body', {
							defaultValue: 'Export was failed on creating. Please try again.',
						})
					)
				}
			})

		props.close()
	}

	return (
		<ExportConfigurationProvider
			kind={DATA_KIND}
			schema={schemaQuery.data || []}
			defaultValues={{
				name: props.defaultName || 'Assortment',
			}}
			isLoading={schemaQuery.isLoading || exportTemplates.isLoading}
			fieldsPresetOptions={fieldsPresetOptions}
			onSubmit={handleSubmit}
		>
			<ExportModal isOpen={props.isOpen} close={props.close} />
		</ExportConfigurationProvider>
	)
}
const isAllColumnsExisted = (
	schemaMap: Record<string, FieldSchemaModel>,
	configs: StoredColumnConfig[] | ColumnConfig[]
) =>
	all(
		(c: StoredColumnConfig | ColumnConfig) => c?.key && schemaMap[c?.key],
		configs
	)
